const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const SCROLL_ANIMATION_INIT_ATTR = 'data-scroll-animate-init';

const SCROLL_OBSERVER_OPTIONS = {
  rootMargin: '0px 0px -8% 0px',
  threshold: 0.08,
};

function getRevealViewportBottom() {
  return window.innerHeight * 0.92;
}

function isInRevealViewport(element) {
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < getRevealViewportBottom();
}

function isHeaderScrollTrigger(element) {
  if (element.tagName === 'STICKY-HEADER') return true;
  return element.classList.contains('section-header');
}

function isAnimatableElement(element) {
  if (isHeaderScrollTrigger(element)) return false;

  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function clearWillChange(event) {
  event.currentTarget.style.willChange = '';
}

function prepareRevealAnimation(element) {
  if (element.classList.contains(SCROLL_ANIMATION_CANCEL_CLASSNAME)) return;

  element.removeEventListener('animationend', clearWillChange);
  element.addEventListener('animationend', clearWillChange, { once: true });

  if (isInRevealViewport(element)) {
    element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    element.style.willChange = 'opacity, transform';
  } else {
    element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    element.style.willChange = '';
  }
}

function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);

      if (elementTarget.hasAttribute('data-cascade') && !elementTarget.style.getPropertyValue('--animation-order')) {
        elementTarget.style.setProperty('--animation-order', index);
      }

      elementTarget.style.willChange = 'opacity, transform';
      elementTarget.removeEventListener('animationend', clearWillChange);
      elementTarget.addEventListener('animationend', clearWillChange, { once: true });

      observer.unobserve(elementTarget);
    } else if (!element.target.hasAttribute(SCROLL_ANIMATION_INIT_ATTR)) {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function resetVisibilityCancelledTriggers() {
  document
    .querySelectorAll(`.${SCROLL_ANIMATION_TRIGGER_CLASSNAME}.${SCROLL_ANIMATION_CANCEL_CLASSNAME}`)
    .forEach((element) => {
      if (!element.hasAttribute(SCROLL_ANIMATION_INIT_ATTR)) return;

      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return;

      element.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.removeAttribute(SCROLL_ANIMATION_INIT_ATTR);
    });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const pendingTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME)).filter(
    (element) => !element.hasAttribute(SCROLL_ANIMATION_INIT_ATTR)
  );

  if (pendingTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    pendingTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
      element.setAttribute(SCROLL_ANIMATION_INIT_ATTR, '');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, SCROLL_OBSERVER_OPTIONS);

  pendingTriggerElements.forEach((element) => {
      if (!isAnimatableElement(element)) {
        element.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
        element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        element.setAttribute(SCROLL_ANIMATION_INIT_ATTR, '');
        return;
      }

      element.setAttribute(SCROLL_ANIMATION_INIT_ATTR, '');
      prepareRevealAnimation(element);
      observer.observe(element);
    });
}

window.initializeScrollAnimationTrigger = initializeScrollAnimationTrigger;

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    return 100;
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

let resizeTimer;
function onResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    resetVisibilityCancelledTriggers();
    initializeScrollAnimationTrigger(document);
  }, 150);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

window.addEventListener('resize', onResize);

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, false));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, false));
}
